import { useEffect, useState, useMemo } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { mutate } from "swr";
import { FormErrorMessage } from "../../components";
import { ApiSchemaVendorSipHeaderModel } from "../../models";
import { PaginationSettings } from "../../configs";
import { useFunctionalityVendorSipHeader } from "../../pages/hooks";

type Props = {
  show: boolean;
  handleShow: (value: boolean) => void;
  vendorSipHeaderUrl: string | null;
  dataToEdit?: ApiSchemaVendorSipHeaderModel;
  setDataToEdit?: (value?: ApiSchemaVendorSipHeaderModel) => void;
};


export function ModalAddSipHeader(props: Props): JSX.Element {
  const {
    vendorSipHeaderList,
    getAllSipHeadersUrl,
    onAddSipHeader,
    onUpdateSipHeader,
  } = useFunctionalityVendorSipHeader({
    shouldVendorSipHeaderGetAllInvoke: true,
    pageSize: PaginationSettings.initialPageSize,
    pageNo: PaginationSettings.initialPageIndex,
    setEditModalShow: props.handleShow,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ApiSchemaVendorSipHeaderModel>({
    defaultValues: {
      headerName: "",
      headerValue: "",
    },
  });

  const isLoading = vendorSipHeaderList === undefined;
  const { dataToEdit, setDataToEdit } = props;

  useEffect(() => {
    if (dataToEdit) {
      reset({
        headerName: dataToEdit.headerName,
        headerValue: dataToEdit.headerValue,
      });
    } else {
      reset({
        headerName: "",
        headerValue: "",
      });
    }
  }, [vendorSipHeaderList, dataToEdit, reset]);

  const sipHeaderOptions = useMemo(
    () => [
      { key: "languageToId", label: "languageToId" },
      { key: "languageFromId", label: "languageFromId" },
      { key: "serviceTypeId", label: "serviceTypeId" },
      { key: "serviceTypeCode", label: "serviceTypeCode" },
      { key: "genderId", label: "genderId" },
      { key: "pin", label: "pin" },
      { key: "callnum", label: "callnum" },
      { key: "clientId", label: "clientId" },
      { key: "meta", label: "meta" },
      { key: "companyId", label: "companyId" },
      { key: "vendorId", label: "vendorId" },
      { key: "companyName", label: "companyName" },
      { key: "clientTin", label: "clientTin" },
      { key: "clientName", label: "clientName" },
    ],
    []
  );


  const usedHeaderValues = useMemo(() => {
    const arr = vendorSipHeaderList ?? [];
    const current = (dataToEdit?.headerValue ?? "").toLowerCase().trim();

    return new Set(
      arr
        .map(h => (h?.headerValue ?? "").toLowerCase().trim())
        .filter(v => v && v !== current)
    );
  }, [vendorSipHeaderList, dataToEdit?.headerValue]);

  const availableSipOptions = useMemo(
    () =>
      sipHeaderOptions.filter(
        opt => !usedHeaderValues.has(opt.key.toLowerCase())
      ),
    [sipHeaderOptions, usedHeaderValues]
  );

  const onCreateHandler = (form: ApiSchemaVendorSipHeaderModel) => {
    onAddSipHeader(form)
      .then(() => {
        mutate(props.vendorSipHeaderUrl || getAllSipHeadersUrl);
        setDataToEdit?.(undefined);
        props.handleShow(false);
      })
      .catch((e: any) => console.error(e));
  };

  const onEditHandler = async (form: ApiSchemaVendorSipHeaderModel) => {
    if (!dataToEdit) return;

    try {
      await onUpdateSipHeader(dataToEdit.id, {
        headerName: form.headerName,
        headerValue: form.headerValue,
      });
      setDataToEdit?.(undefined);
      props.handleShow(false);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Modal
      show={props.show}
      onHide={() => { setDataToEdit?.(undefined); props.handleShow(false) }}
      centered
      id="addVendorSipHeaderModal"
    >
      <Modal.Header closeButton>
        <Modal.Title>{dataToEdit ? "Edit SIP Header" : "Add SIP Header"}</Modal.Title>
      </Modal.Header>

      <form onSubmit={handleSubmit(dataToEdit ? onEditHandler : onCreateHandler)}>
        <Modal.Body>
          <div className="row">
            <div className="col-12">
              <div className="form-group">
                <label className="position-relative required">Header Name</label>
                <input
                  type="text"
                  {...register("headerName", {
                    required: true,
                    validate: v =>
                      (v?.trim().length ?? 0) > 0 || "Header name cannot be blank",
                  })}
                  className="form-control"
                  placeholder="Vendor Header Name"
                />
                {errors.headerName && <FormErrorMessage message="required" />}
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="form-group">
                <label className="position-relative required">Header Value</label>

                {isLoading && (
                  <div className="d-flex align-items-center gap-2 mb-2" aria-live="polite">
                    <Spinner animation="border" size="sm" />
                    <span className="text-muted small ml-2">Loading headers…</span>
                  </div>
                )}
                <select
                  id="headerValue"
                  className={`form-control ${errors.headerValue ? "is-invalid" : ""
                    }`}
                  {...register("headerValue", {
                    required: "Header value is required",
                    validate: v => {
                      const key = (v ?? "").toLowerCase().trim();
                      if (!key) return "Header value cannot be blank";
                      return (
                        !usedHeaderValues.has(key) ||
                        "This header value is already in use"
                      );
                    },
                  })}
                  disabled={
                    isLoading ||
                    (availableSipOptions.length === 0 && !dataToEdit)
                  }
                >
                  <option value="" disabled>
                    — Select a SIP value —
                  </option>

                  {availableSipOptions.map(opt => (
                    <option key={opt.key} value={opt.key}>
                      {opt.label}
                    </option>
                  ))}
                </select>

                {!isLoading && availableSipOptions.length === 0 && (
                  <small className="text-muted">
                    All predefined SIP values are already in use for this vendor.
                  </small>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="btn btn-link text-site text-uppercase"
            onClick={() => {
              setDataToEdit?.(undefined);
              props.handleShow(false);
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary text-uppercase login-from-submit-btn"
            disabled={
              isLoading ||
              (availableSipOptions.length === 0 && !dataToEdit)
            }
          >
            {dataToEdit ? "Save" : "Create"}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
